import React, { useState } from "react";
import CreateTestModal from "../../../components/CreateTestModal";
import { addStandardCourseItem, deleteStandardCourseItem, updateStandardCourseItem } from "../../../api/backend";
import { Notice } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { EmptyNote, PanelHeader, SessionSelect, errorText } from "./common";

const ROW = { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: "1px solid var(--ga-line)", flexWrap: "wrap" };
const ICON = { width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", background: "var(--ga-soft)", color: "var(--ga-sub)", flexShrink: 0 };

/**
 * Written tests of a standard course, built with the same test builder as every other test. Every question needs a
 * question and a correct answer: a training center cannot switch on a test that has incomplete questions.
 */
export default function TestsPanel({ draft, courseId, onChanged }) {
  const [builder, setBuilder] = useState(null);
  const [error, setError] = useState(null);
  const tests = draft.tests;

  const attempt = async (action, fallback) => {
    setError(null);
    try {
      const result = await action();
      onChanged();
      return result;
    } catch (e) {
      setError(errorText(e, fallback));
      throw e;
    }
  };

  // An auto-save may already have created this test; its id comes back in savedBankId, so it is updated, not copied.
  const save = ({ autosave, savedBankId, ...payload }) => {
    const id = builder.id ?? savedBankId ?? null;
    return attempt(
      () => (id
        ? updateStandardCourseItem(courseId, "tests", id, payload)
        : addStandardCourseItem(courseId, "tests", payload)),
      "The test could not be saved.");
  };

  return (
    <div className="panel" data-testid="tests-panel">
      <PanelHeader title="Tests" subtitle="Taken by students at every training center that runs this course."
        action={<button type="button" className="b pri sm" onClick={() => setBuilder({})}><I.plus s={12} />Add test</button>} />
      <div className="panel-b" style={{ padding: 0 }}>
        {error && (
          <div style={{ padding: "12px 18px 0" }}>
            <Notice kind="bad">
              {error}{" "}
              <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
            </Notice>
          </div>
        )}
        {tests.length === 0 ? <EmptyNote>No tests yet.</EmptyNote> : tests.map((t) => (
          <div key={t.id} data-testid={`test-${t.id}`} style={ROW}>
            <span style={ICON}><I.check s={16} /></span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700 }}>{t.title}</div>
              <div className="sub" style={{ fontSize: 12.5 }}>
                {(t.questions || []).length} questions{t.passingScore ? ` · pass at ${t.passingScore}%` : ""}
                {t.maxRetakes != null ? ` · ${t.maxRetakes} ${t.maxRetakes === 1 ? "retake" : "retakes"}` : ""}
              </div>
            </div>
            <SessionSelect sessions={draft.sessions} value={t.sessionId}
              onChange={(sessionId) => attempt(() => updateStandardCourseItem(courseId, "tests", t.id, { sessionId }), "The test could not be moved.").catch(() => {})} />
            <button type="button" className="b sec sm" onClick={() => setBuilder(t)}><I.pencil s={12} />Edit</button>
            <button type="button" className="ib" aria-label={`Remove ${t.title}`}
              onClick={() => attempt(() => deleteStandardCourseItem(courseId, "tests", t.id), "The test could not be removed.").catch(() => {})}><I.x s={16} /></button>
          </div>
        ))}
      </div>
      {builder && <CreateTestModal open bank initial={builder.id ? builder : undefined} onClose={() => setBuilder(null)} onSave={save} />}
    </div>
  );
}
